import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  ProgressBar,
  Modal,
  Spinner,
} from "react-bootstrap";

import { useNavigate } from "react-router-dom";

import { BsStarFill } from "react-icons/bs";
import { MdOutlineFeedback } from "react-icons/md";

import { toast } from "react-toastify";
import { TUTOR_FEEDBACK_API } from "../../../api/api";

import Announcementsimg from "../../../assets/announcements-empty.png";

// import allAssignmentsIcon from "../../../assets/all-assignments-icon.png";
// import NoOfCoursesIcon from "../../../assets/no-of-courses-icon.png";
// import pendingReviewIcon from "../../../assets/pending-review-icon.png";
// import gradedIcon from "../../../assets/grade-icon.png";

// latestIcons
import averageRating from "../../../assets/icons/tutor/average-rating.svg";
import totalReviews from "../../../assets/icons/tutor/total-reviews.svg";
import fiveStarRatings from "../../../assets/icons/tutor/5-star-rating.svg";
import responseRate from "../../../assets/icons/tutor/feedback-response-rate.svg";

import { handleApiError } from "../../../utils/apiErrorHandler";

import "./TutorFeedback.css";

const TutorFeedback = () => {
  const navigate = useNavigate();

  const [reviews, setReviews] = useState([]);
  // const [ratingSummary, setRatingSummary] = useState({});
  const [reviewStats, setReviewStats] = useState({});

  const [selectedReview, setSelectedReview] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [response, setResponse] = useState("");

  const [filterRating, setFilterRating] = useState("All Ratings");
  const [filterCourse, setFilterCourse] = useState("All Courses");
  const [distributionCourse, setDistributionCourse] = useState("All Courses");

  const [courses, setCourses] = useState([]);

  //loading
  const [feedbackLoading, setFeedbackLoading] = useState(true);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setFeedbackLoading(false);
      navigate("/signin");
      return;
    }

    const fetchTutorFeedbacks = async () => {
      try {
        const response = await axios.post(
          TUTOR_FEEDBACK_API.GET_TUTOR_FEEDBACKS,
          { tutor_id: userId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = response.data;

        if (data.statusCode === 200) {
          //  normalize everything here
          const normalized = (data.data || []).map((r) => ({
            ...r,
            course_title: r.course_title?.trim(),
            rating: Number(r.rating),
          }));
          setReviews(normalized);

          setReviewStats({
            ...data.stats,
            average_rating: Number(data.stats?.average_rating || 0),
            response_rate: Number(data.stats?.response_rate || 0),
          });

          // Extract unique course titles from the feedback data
          const courseTitles = [
            ...new Set(normalized.map((r) => r.course_title)),
          ];
          setCourses(courseTitles);
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error("Error fetching feedback:", error);
        // Server responded with non-2xx status (like 400, 500)

        // CHECK (expiry / invalid token)
        handleApiError(error, navigate, showErrorToast, "/signin");
      } finally {
        setFeedbackLoading(false);
      }
    };

    fetchTutorFeedbacks();
  }, [navigate]);

  // const ratingDistribution = {
  //   5: Number(ratingSummary.total_5_rating),
  //   4: Number(ratingSummary.total_4_rating),
  //   3: Number(ratingSummary.total_3_rating),
  //   2: Number(ratingSummary.total_2_rating),
  //   1: Number(ratingSummary.total_1_rating),
  // };

  const renderStars = (count) => {
    return Array.from({ length: 5 }, (_, i) =>
      i < count ? (
        <BsStarFill key={i} className="tutor-feedback-review-stars-filled" />
      ) : (
        <BsStarFill key={i} className="tutor-feedback-review-stars-empty" />
      ),
    );
  };

  const filteredReviews = useMemo(() => {
    return reviews.filter((review) => {
      const isRatingMatch =
        filterRating === "All Ratings" ||
        review.rating === Number(filterRating);

      const isCourseMatch =
        filterCourse === "All Courses" || review.course_title === filterCourse;

      return isRatingMatch && isCourseMatch;
    });
  }, [reviews, filterRating, filterCourse]);

  const distributionData = useMemo(() => {
    const source =
      distributionCourse === "All Courses"
        ? reviews
        : reviews.filter((r) => r.course_title === distributionCourse);

    const dist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

    source.forEach((r) => {
      const rating = Number(r.rating);
      if (dist[rating] !== undefined) {
        dist[rating]++;
      }
    });

    return {
      dist,
      total: source.length,
    };
  }, [reviews, distributionCourse]);

  // Show the modal and set the selected review
  const handleShowFeedback = (review) => {
    setSelectedReview(review);
    setResponse(review.response || "");
    setShowModal(true);
  };

  // Close the modal
  const handleClose = () => {
    setShowModal(false);
    setResponse("");
  };

  // Handle response submission
  const handleSendResponse = async () => {
    const feedbackId = selectedReview?.feedback_id;
    if (!feedbackId) {
      return;
    }

    if (!response || !/[a-zA-Z]/.test(response)) {
      showErrorToast("Response must contain at least one letter");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/signin");
      return;
    }

    try {
      const FeedbackResponse = await axios.post(
        TUTOR_FEEDBACK_API.RESPOND_FEEDBACK,
        { feedback_id: feedbackId, response: response },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = FeedbackResponse.data;
      // Handle successful response
      if (data.statusCode === 200) {
        const updatedReviews = reviews.map((review) =>
          review.feedback_id === selectedReview.feedback_id
            ? { ...review, response: response }
            : review,
        );

        setReviews(updatedReviews);
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });
        handleClose();
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error saving response:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    }
  };

  const clearFiltersFeedback = () => {
    setFilterRating("All Ratings");
    setFilterCourse("All Courses");
  };

  if (feedbackLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container className="tutor-feedback-container">
      <h4 className="tutor-feedback-title">Student Feedback</h4>
      <p className="tutor-feedback-subtitle">
        View student reviews and ratings.
      </p>

      {/* Stats Row */}
      <Row className="tutor-assignment-cards-row" style={{ gap: "0px" }}>
        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="tutor-myassignment-info-card">
            <h6>Average Rating</h6>
            <img
              src={averageRating}
              alt="Avg Rating"
              className="tutor-assignment-info-card-icon"
            />
            <h3>{reviewStats.average_rating || "-"}</h3>
            <span className="tutor-assignment-card-link-green">
              +8% from last month
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="tutor-myassignment-info-card">
            <h6>Total Reviews</h6>
            <img
              src={totalReviews}
              alt="reviews"
              className="tutor-assignment-info-card-icon"
            />
            <h3>{reviewStats.total_reviews || "-"}</h3>
            <span className="tutor-assignment-card-link-green">
              +5% from last month
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="tutor-myassignment-info-card">
            <h6>5-Star Ratings</h6>
            <img
              src={fiveStarRatings}
              alt="star rating"
              className="tutor-assignment-info-card-icon"
            />
            <h3>{reviewStats.five_star || "-"}</h3>
            <span className="tutor-assignment-card-link-green">
              +23% this week
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="tutor-myassignment-info-card">
            <h6>Response Rate</h6>
            <img
              src={responseRate}
              alt="response rate"
              className="tutor-assignment-info-card-icon"
            />
            <h3>{reviewStats.response_rate || "-"}</h3>
            <span className="tutor-assignment-card-link-gray">
              Based on 245 reviews
            </span>
          </div>
        </Col>
      </Row>

      {/* Rating Distribution */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="tutor-feedback-rating-title mb-0">
          Rating Distribution
        </h6>
        <Form.Select
          className="tutor-feedback-filter-select"
          value={distributionCourse}
          onChange={(e) => setDistributionCourse(e.target.value)}
        >
          <option>All Courses</option>
          {courses.map((c, i) => (
            <option key={i} value={c}>
              {c}
            </option>
          ))}
        </Form.Select>
      </div>
      <div className="tutor-feedback-rating-section">
        {Object.entries(distributionData.dist)
          .sort((a, b) => b[0] - a[0])
          .map(([star, count]) => (
            <Row key={star} className="tutor-feedback-rating-row">
              <Col xs="auto" className="tutor-feedback-rating-star">
                <span className="tutor-feedback-rating-star-number">
                  {star}
                </span>
                <BsStarFill className="tutor-feedback-rating-star-icon" />
              </Col>
              <Col xs={10}>
                <ProgressBar
                  now={
                    distributionData.total
                      ? (count / distributionData.total) * 100
                      : 0
                  }
                  className="tutor-feedback-rating-progress"
                />
              </Col>
              <Col xs="auto" className="tutor-feedback-rating-count">
                {count} reviews
              </Col>
            </Row>
          ))}
      </div>

      {/* Filters */}
      <div className="tutor-feedback-filter-row">
        <Form.Select
          value={filterRating}
          onChange={(e) => setFilterRating(e.target.value)}
          className="tutor-feedback-filter-select"
        >
          <option value="All Ratings">All Ratings</option>
          <option value="5">5 Stars</option>
          <option value="4">4 Stars</option>
          <option value="3">3 Stars</option>
          <option value="2">2 Stars</option>
          <option value="1">1 Star</option>
        </Form.Select>

        <Form.Select
          value={filterCourse}
          onChange={(e) => setFilterCourse(e.target.value)}
          className="tutor-feedback-filter-select"
        >
          <option value="All Courses">All Courses</option>
          {courses.map((course, index) => (
            <option key={index} value={course}>
              {course}
            </option>
          ))}
        </Form.Select>

        <Button
          // size="sm"
          className="tutor-analytics-section-student-export"
          onClick={clearFiltersFeedback}
        >
          Clear
        </Button>
      </div>

      {/* Reviews */}
      {filteredReviews.length === 0 ? (
        <div className="tutor-feedback-no-reviews mt-4">
          <div className="tutor-assignment-empty-content">
            <img
              src={Announcementsimg}
              alt="empty"
              className="tutor-announcements-section-empty-img mb-3"
            />
            <h5>No reviews available.</h5>
            <p>Student reviews and ratings will appear here.</p>
          </div>
        </div>
      ) : (
        <div className="tutor-feedback-reviews-list">
          {filteredReviews.map((review, index) => (
            <Card
              key={review.feedback_id}
              className="tutor-feedback-review-card"
            >
              <Row className="align-items-start">
                {/* Number Box */}
                <Col xs="auto" className="tutor-feedback-review-number-box">
                  {index + 1 < 10 ? `0${index + 1}` : index + 1}
                </Col>

                <Col className="tutor-feedback-review-content">
                  <div className="tutor-feedback-reviewer-name">
                    {review.student_name}
                  </div>
                  <div className="tutor-feedback-review-course">
                    {review.course_title}
                  </div>
                  <div className="tutor-feedback-review-comment">
                    {review.review}
                  </div>

                  {/* Show Response Box */}
                  {review.response && (
                    <div className="tutor-feedback-response-box">
                      <div className="tutor-feedback-response-title">
                        Your Response:
                      </div>

                      <div className="tutor-feedback-response-text">
                        {review.response}
                      </div>
                    </div>
                  )}

                  <Button
                    size="sm"
                    className={
                      review.response
                        ? "tutor-feedback-update-btn"
                        : "tutor-feedback-review-btn"
                    }
                    onClick={() => handleShowFeedback(review)}
                  >
                    <MdOutlineFeedback />{" "}
                    {review.response
                      ? "Update Response"
                      : "Respond to Feedback"}
                  </Button>
                </Col>

                <Col xs="auto" className="tutor-feedback-review-rating-date">
                  <div className="tutor-feedback-review-stars">
                    {renderStars(review.rating)}
                  </div>
                  <div className="tutor-feedback-review-date">
                    {review.feedback_created_at
                      ? new Date(review.feedback_created_at)
                          .toISOString()
                          .split("T")[0]
                      : "-"}
                  </div>
                </Col>
              </Row>
            </Card>
          ))}
        </div>
      )}

      {/* Modal to Respond to Feedback */}
      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        backdrop="static"
        keyboard={false}
        className="tutor-feedback-response-modal p-0"
      >
        <Modal.Header className="tutor-feedback-response-modal-header">
          <button
            className="tutor-feedback-response-modal-close-btn"
            onClick={() => setShowModal(false)}
            aria-label="Close"
          >
            ✕
          </button>
          <div className="tutor-feedback-response-modal-title-block">
            <h5>Respond to Student Feedback</h5>
            <p className="tutor-feedback-response-modal-subtitle">
              Send a personalized response to {selectedReview?.student_name}'s
              feedback
            </p>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="tutor-feedback-response-modal-box">
            <span className="tutor-feedback-response-modal-review-stars">
              {renderStars(selectedReview?.rating)}{" "}
            </span>
            <p className="tutor-feedback-response-modal-comment">
              {selectedReview?.review}
            </p>
          </div>
          <Form>
            <Form.Group controlId="responseText">
              <Form.Label className="tutor-feedback-response-modal-label">
                Your Response *
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                placeholder="Write your response..."
                className="tutor-feedback-response-modal-textarea"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="tutor-feedback-response-modal-footer">
          <Button
            onClick={handleClose}
            className="tutor-feedback-response-modal-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendResponse}
            className="tutor-feedback-response-modal-btn"
          >
            {selectedReview?.response ? "Update Response" : "Send Response"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TutorFeedback;
